import { useApolloClient } from "@apollo/client";
import moment from "moment";
import { useState } from "react";
import { AttendeeRole, AttendeeStatus } from "types/graphql-schema";

import MeetingDialog from "@apps/meeting-dialog/meeting-dialog";
import useLabel from "@apps/use-label/use-label";
import { currentUserVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";

import getProfileOneononesQuery from "./graphql/get-profile-oneonones-query";

// How long a 1-on-1 started on the spot is assumed to run. Just the dialog's
// starting point — the user can change it before saving.
const STARTED_NOW_DURATION_MINUTES = 30;

// The other person in the 1-on-1 (the non-subject participant, who also
// facilitates). Same shape as OneononeCounterpart in
// oneonone-meeting-empty-cards, plus the fields the meeting dialog needs to
// render an attendee row.
export type OneononeDialogCounterpart = {
  id: number;
  firstName: string | null | undefined;
  name?: string | null;
  email: string;
  avatar?: string | null;
};

// Shown at the top of the profile 1-on-1 tab when the person has past 1-on-1s
// but nothing scheduled ahead. Opens the standard "New meeting" dialog,
// pre-filled as a 1-on-1 between the two people and starting now, so the user
// can either save a draft to take notes immediately or publish it to calendars.
const StartOneononeNowCard = ({
  profileId,
  profileName,
  profileFirstName,
  profileEmail,
  profileAvatar,
  profileManager,
  onStarted,
}: {
  profileId: number;
  profileName: string | null | undefined;
  profileFirstName: string | null | undefined;
  profileEmail: string | undefined;
  profileAvatar?: string | null;
  // The profile user's primary manager. Only used (and only present) on the
  // current user's own profile, where the manager is the 1-on-1 counterpart.
  profileManager?: OneononeDialogCounterpart | null;
  onStarted: (newMeetingId: number) => void;
}) => {
  const label = useLabel();
  const currentUser = currentUserVar();
  const apolloClient = useApolloClient();
  const [showDialog, setShowDialog] = useState(false);

  // A 1-on-1 always has two people: the subject (the profile) and a counterpart
  // who facilitates. On a report's profile the counterpart is the current user;
  // on the current user's OWN profile it's their manager. Facilitating the
  // profile user themselves isn't a valid 1-on-1, so we never make the current
  // user the counterpart of their own profile.
  const isOwnProfile = profileId === currentUser.id;
  const counterpart: OneononeDialogCounterpart | null = isOwnProfile
    ? (profileManager ?? null)
    : currentUser;

  const handleSaved = ({ meeting }: { meeting?: { id: number } | null }) => {
    // The tab's own list query is SKIPPED while the URL names a meeting (which
    // it does whenever this card is visible), so it is not active and neither
    // refetchQueries nor a plain refetch reaches it. Forget the cached
    // `meetings` root fields instead, so the new 1-on-1 is there when the user
    // leaves the tab and comes back.
    apolloClient.cache.evict({ id: "ROOT_QUERY", fieldName: "meetings" });
    if (meeting) onStarted(meeting.id);
  };

  // Nobody to start the 1-on-1 with (own profile, no manager set up). The
  // Overview tab is where that gets fixed, so stay quiet here rather than
  // offering an action that can't succeed.
  if (!counterpart || profileEmail === undefined) return null;

  const startDatetime = moment();

  return (
    <>
      {/* @container so the row stacks based on the notes width (e.g. when the
          Ask AI sidebar is open), not the viewport — same rule the meeting
          header uses. */}
      <div className="@container bg-gray-50 border border-gray-100 rounded-[10px] px-6 py-4 mb-6">
        <div className="flex flex-col @xl:flex-row @xl:items-center gap-4 justify-between">
          <div className="flex items-start @xl:items-center gap-3 min-w-0">
            <TopicflowIcon
              type="calendarPlus"
              className="w-5 h-5 text-blue-500 shrink-0 mt-0.5 @xl:mt-0"
            />
            <div className="flex flex-col min-w-0">
              <div className="tf-header-base-dark">
                No upcoming {label("1-on-1")} scheduled
              </div>
              <div className="tf-body-sm-light">
                Start one now to capture notes right away.
              </div>
            </div>
          </div>
          <Button
            className="js-test-start-oneonone-now shrink-0"
            theme={buttonTheme.primary}
            size={ButtonSize.large}
            text={`Start a ${label("1-on-1")} now`}
            onClick={() => setShowDialog(true)}
          />
        </div>
      </div>

      {showDialog && (
        <MeetingDialog
          formOptions={{
            title: `${label("1-on-1")}: ${counterpart.firstName} <> ${profileFirstName}`,
            isFormalOneonone: true,
            // Starting now rather than the dialog's usual next-hour default:
            // this card is for a 1-on-1 that is happening right away.
            startDatetime: startDatetime.format(),
            endDatetime: startDatetime
              .clone()
              .add(STARTED_NOW_DURATION_MINUTES, "minutes")
              .format(),
            attendees: [
              {
                id: counterpart.id,
                email: counterpart.email,
                name: counterpart.name || counterpart.firstName || "",
                avatar: counterpart.avatar,
                role: AttendeeRole.Required,
                participantStatus: AttendeeStatus.NotResponded,
              },
              {
                id: profileId,
                email: profileEmail,
                name: profileName || profileFirstName || profileEmail,
                avatar: profileAvatar,
                role: AttendeeRole.Required,
                participantStatus: AttendeeStatus.NotResponded,
              },
            ],
            // The counterpart facilitates so the profile user stays the subject
            // — a meeting a person facilitates for themselves isn't a valid
            // 1-on-1.
            facilitatorId: counterpart.id,
          }}
          refetchQueries={[getProfileOneononesQuery]}
          onSaved={handleSaved}
          onClose={() => setShowDialog(false)}
        />
      )}
    </>
  );
};

export default StartOneononeNowCard;
